import { ExclamationCircleTwoTone } from '@ant-design/icons';
import { ConfirmModal, SimpleForm } from '@evidia/components';
import type { DrivingLessonRecordType } from '@evidia/shared';
import { DrivingLessonRecordState } from '@evidia/shared';
import { Button, notification } from 'antd';
import styled from 'styled-components';
import { useActiveSchoolId } from '../../../../hooks/useActiveSchoolId';
import { mergeDateAndTime } from '../../../../utils/date';
import { trpc } from '../../../../utils/trcp';
import MainTitle from '../../../MainTitle';
import type { DrivingLessonsRecord } from './TrainingTab';
import useTrainingFields from './useTrainingFileds';

const Title = styled(MainTitle)`
	text-align: left;
`;

const Footer = styled.div`
	display: flex;
	justify-content: center;
	gap: 10px;
	margin-top: 10px;
`;

interface Props {
	activeDrivingLessonsRecord?: DrivingLessonsRecord;
	studentId: string;
	isEdit?: boolean;
	onClose: () => void;
}

const ActiveTrainingPlan: React.FC<Props> = ({ isEdit, onClose, activeDrivingLessonsRecord, studentId }) => {
	const { activeSchoolId } = useActiveSchoolId();
	const updatePlanDrivingLessonsRecordV2 = trpc.drivingLessonsRecord.updatePlanDrivingLessonsRecordV2.useMutation();
	const deleteDrivingLessonsRecord = trpc.drivingLessonsRecord.deleteDrivingLessonsRecord.useMutation();

	const onFormFinish = async (values: {
		startTimeDate: Date;
		startTimeTime: Date;
		length: string;
		endTimeDate: Date;
		endTimeTime: Date;
		teacher: string;
		car: string;
		signature?: string;
		teacherSignature?: string;
		tachometerBefore: number;
		tachometerAfter: number;
		state?: DrivingLessonRecordState;
		type: DrivingLessonRecordType;
	}) => {
		const startTime = mergeDateAndTime(values.startTimeDate, values.startTimeTime);
		const endTime = mergeDateAndTime(values.endTimeDate, values.endTimeTime);
		await updatePlanDrivingLessonsRecordV2.mutateAsync({
			schoolId: activeSchoolId,
			id: activeDrivingLessonsRecord!.id,
			//
			startTime,
			length: values.length ? parseInt(values.length) : undefined,
			endTime,
			userId: values.teacher === '__empty' ? undefined : values.teacher,
			schoolCarId: values.car === '__empty' ? undefined : values.car,
			tachometerBefore: values.tachometerBefore ?? undefined,
			tachometerAfter: values.tachometerAfter ?? undefined,
			signature: values.signature,
			teacherSignature: values.teacherSignature,
			studentId,
			state: values.state ?? DrivingLessonRecordState.COMPLETED,
		});

		notification.success({
			message: 'Jízda zaevidována',
		});
		onClose();
	};

	const {
		fields: formFields,
		trainingFieldsMap,
		triggers,
	} = useTrainingFields({
		type: 'plan',
		editId: activeDrivingLessonsRecord?.id,
		referer: 'studen-card',
	});

	if (!activeDrivingLessonsRecord) {
		return 'Načítám...';
	}

	return (
		<>
			<Title>Zaevidovat výuku</Title>
			<SimpleForm
				initialValues={{
					type: activeDrivingLessonsRecord.type,
					startTimeDate: activeDrivingLessonsRecord.startTime,
					startTimeTime: activeDrivingLessonsRecord.startTime,
					length: `${activeDrivingLessonsRecord.length}`,
					endTimeDate: activeDrivingLessonsRecord.endTime,
					endTimeTime: activeDrivingLessonsRecord.endTime,
					teacher: activeDrivingLessonsRecord.user?.id,
					car: activeDrivingLessonsRecord.schoolCar?.id,
					tachometerBefore: activeDrivingLessonsRecord.tachometerBefore,
					tachometerAfter: activeDrivingLessonsRecord.tachometerAfter,
					signature: activeDrivingLessonsRecord.signature ?? undefined,
					teacherSignature: activeDrivingLessonsRecord.teacherSignature ?? undefined,
					state: activeDrivingLessonsRecord.state,
				}}
				fields={formFields}
				onFinish={onFormFinish}
				fieldsGridMap={trainingFieldsMap}
				submitButtonTitle={isEdit ? 'Uložit změny' : 'Zaevidovat výuku'}
				triggers={triggers}
			/>
			{isEdit && (
				<Footer>
					<ConfirmModal
						title={
							<>
								<ExclamationCircleTwoTone twoToneColor="#ff0000" />
								{' Odstranit výuku'}
							</>
						}
						description="Opravdu chcete odstranit výuku?"
						onConfirm={async () => {
							await deleteDrivingLessonsRecord.mutateAsync({
								id: activeDrivingLessonsRecord.id,
								schoolId: activeSchoolId,
							});
							onClose();
						}}
						okText="Ano"
						cancelText="Zrušit"
					>
						<Button danger>Smazat</Button>
					</ConfirmModal>
					<Button onClick={onClose}>Zrušit</Button>
				</Footer>
			)}
		</>
	);
};

export default ActiveTrainingPlan;
